import { Failure } from '../../../models/common';
import { UnionValueMap } from '../../../types/union-map';
import { ActionName, ActionType, ErrorPayload, FailedPayload, IndexedPayload } from '../core-actions';
import {
    dismissContent,
    dismissQuickAction,
    initializeApplication,
    setWelcomeTourStatus,
    syncDismissedContent,
    syncWelcomeTourStatus,
} from './application-action-creators';

/**
 * Action types
 */

type ApplicationActionName =
    | ActionName<'DismissContent', 'Error' | 'Failed' | 'Success'>
    | ActionName<'DismissQuickAction', 'Error' | 'Failed' | 'Success'>
    | 'FatalError'
    | ActionName<'InitializeApplication', 'Error' | 'Failed' | 'Success'>
    | ActionName<'SyncDismissedContent', 'Error' | 'Failed' | 'Success'>
    | ActionName<'SetWelcomeTourStatus', 'Error' | 'Failed' | 'Success'>
    | ActionName<'SyncWelcomeTourStatus', 'Error' | 'Failed' | 'Success'>;

export type ApplicationActionType =
    | ActionType<'DISMISS_CONTENT', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'DISMISS_QUICK_ACTION', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | 'FATAL_ERROR'
    | ActionType<'INITIALIZE_APPLICATION', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'SYNC_DISMISSED_CONTENT', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'SET_WELCOME_TOUR_STATUS', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'SYNC_WELCOME_TOUR_STATUS', '_ERROR' | '_FAILED' | '_SUCCESS'>;

export const ApplicationActionType: UnionValueMap<ApplicationActionName, ApplicationActionType> = {
    DismissContent: 'DISMISS_CONTENT',
    DismissContentError: 'DISMISS_CONTENT_ERROR',
    DismissContentFailed: 'DISMISS_CONTENT_FAILED',
    DismissContentSuccess: 'DISMISS_CONTENT_SUCCESS',
    DismissQuickAction: 'DISMISS_QUICK_ACTION',
    DismissQuickActionError: 'DISMISS_QUICK_ACTION_ERROR',
    DismissQuickActionFailed: 'DISMISS_QUICK_ACTION_FAILED',
    DismissQuickActionSuccess: 'DISMISS_QUICK_ACTION_SUCCESS',
    FatalError: 'FATAL_ERROR',
    InitializeApplication: 'INITIALIZE_APPLICATION',
    InitializeApplicationError: 'INITIALIZE_APPLICATION_ERROR',
    InitializeApplicationFailed: 'INITIALIZE_APPLICATION_FAILED',
    InitializeApplicationSuccess: 'INITIALIZE_APPLICATION_SUCCESS',
    SyncDismissedContent: 'SYNC_DISMISSED_CONTENT',
    SyncDismissedContentError: 'SYNC_DISMISSED_CONTENT_ERROR',
    SyncDismissedContentFailed: 'SYNC_DISMISSED_CONTENT_FAILED',
    SyncDismissedContentSuccess: 'SYNC_DISMISSED_CONTENT_SUCCESS',
    SetWelcomeTourStatus: 'SET_WELCOME_TOUR_STATUS',
    SetWelcomeTourStatusError: 'SET_WELCOME_TOUR_STATUS_ERROR',
    SetWelcomeTourStatusFailed: 'SET_WELCOME_TOUR_STATUS_FAILED',
    SetWelcomeTourStatusSuccess: 'SET_WELCOME_TOUR_STATUS_SUCCESS',
    SyncWelcomeTourStatus: 'SYNC_WELCOME_TOUR_STATUS',
    SyncWelcomeTourStatusError: 'SYNC_WELCOME_TOUR_STATUS_ERROR',
    SyncWelcomeTourStatusFailed: 'SYNC_WELCOME_TOUR_STATUS_FAILED',
    SyncWelcomeTourStatusSuccess: 'SYNC_WELCOME_TOUR_STATUS_SUCCESS',
};

/**
 * Payloads
 */

export type DismissContentPayload = IndexedPayload<'content'>;
export type DismissContentErrorPayload = ErrorPayload & IndexedPayload<'content'>;
export type DismissContentFailedPayload = FailedPayload & IndexedPayload<'content'>;
export type DismissContentSuccessPayload = IndexedPayload<'content'>;

export type DismissQuickActionPayload = IndexedPayload<'quickAction'>;
export type DismissQuickActionErrorPayload = ErrorPayload & IndexedPayload<'quickAction'>;
export type DismissQuickActionFailedPayload = FailedPayload & IndexedPayload<'quickAction'>;
export type DismissQuickActionSuccessPayload = IndexedPayload<'quickAction'>;

export type SetWelcomeTourStatusPayload = { seenOrSkippedWelcomeTour: boolean };

export type SyncWelcomeTourStatusPayload = { seenOrSkippedWelcomeTour?: boolean }; // This is optional since net new users won't have anything set

export interface FatalErrorPayload {
    failure: Failure;
}

/**
 * Action shorthands
 */

export type DismissContentAction = ReturnType<typeof dismissContent>;
export type DismissQuickActionAction = ReturnType<typeof dismissQuickAction>;
export type InitializeApplicationAction = ReturnType<typeof initializeApplication>;
export type SyncDismissedContentAction = ReturnType<typeof syncDismissedContent>;
export type SetWelcomeTourStatusAction = ReturnType<typeof setWelcomeTourStatus>;
export type SyncWelcomeTourStatusAction = ReturnType<typeof syncWelcomeTourStatus>;
